import { Alert, AlertIcon, Box, Button, FormControl, Heading, Stack, Text, Textarea } from "@chakra-ui/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  type MarketDialogueQuestionDto,
  type ReceivedMarketDialogueAnswersDto,
  useSummariseMarketDialogueMutation,
} from "../../autogen/bff-api";
import { useApiError } from "../../common/errors/useApiError";
import { useDisplayDateWithTime } from "../../common/formatting/useDisplayDateWithTime";
import { TextLengthHint } from "../../common/input/TextLengthHint";
import { TakenForwardAnswers } from "./TakenForwardAnswers";

const MAX_SUMMARY_LENGTH = 20000;

interface Props {
  marketDialogueId: string;
  questions: MarketDialogueQuestionDto[];
  data: ReceivedMarketDialogueAnswersDto;
}

export const MarketDialogueInsightReport = ({
  marketDialogueId,
  questions,
  data,
}: Props) => {
  const { t } = useTranslation();
  const displayDateWithTime = useDisplayDateWithTime();
  if (!data.canSeeInsights) {
    return (
      <Alert status="info" borderRadius="md" fontSize="sm" data-testid="insight-report-team-only">
        <AlertIcon />
        {t("Only the market dialogue team sees the summary and the answers taken forward.")}
      </Alert>
    );
  }
  return (
    <Stack spacing="6" data-testid="insight-report">
      <Box>
        <Heading as="h3" size="sm" mb="2">
          {t("Summary of the dialogue")}
        </Heading>
        {data.canMark ? (
          <SummaryEditor
            key={data.summary?.writtenAt ?? "none"}
            marketDialogueId={marketDialogueId}
            initial={data.summary?.text ?? ""}
          />
        ) : data.summary ? (
          <Text whiteSpace="pre-wrap">{data.summary.text}</Text>
        ) : (
          <Text color="smMuted">{t("No summary has been written yet.")}</Text>
        )}
        {data.summary && (
          <Text fontSize="xs" color="smMuted" mt="1">
            {t("Written by {{name}}, {{time}}", {
              name: data.summary.writtenBy,
              time: displayDateWithTime(data.summary.writtenAt),
            })}
          </Text>
        )}
      </Box>
      <Box>
        <Heading as="h3" size="sm" mb="2">
          {t("Answers taken forward")}
        </Heading>
        <TakenForwardAnswers
          questions={questions}
          answers={data.answers}
          empty={t("No answers are taken forward yet. Choose «Take forward» on an answer under «Per question».")}
        />
      </Box>
    </Stack>
  );
};

const SummaryEditor = ({
  marketDialogueId,
  initial,
}: {
  marketDialogueId: string;
  initial: string;
}) => {
  const { t } = useTranslation();
  const { trigger } = useApiError();
  const [text, setText] = useState(initial);
  const [summarise, { isLoading }] = useSummariseMarketDialogueMutation();
  const save = async () => {
    try {
      await summarise({
        marketDialogueId,
        summariseMarketDialogueRequest: { text },
      }).unwrap();
    } catch (e) {
      trigger(e);
    }
  };
  return (
    <Stack spacing="2">
      <FormControl isInvalid={text.length > MAX_SUMMARY_LENGTH}>
        <Textarea
          aria-label={t("Summary of the dialogue") ?? undefined}
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={6}
          maxLength={MAX_SUMMARY_LENGTH}
          placeholder={
            t(
              "What did the market agree on, and what will you take into the competition?",
            ) ?? undefined
          }
        />
        <TextLengthHint text={text} max={MAX_SUMMARY_LENGTH} />
      </FormControl>
      <Button
        alignSelf="flex-start"
        size="sm"
        colorScheme="teal"
        onClick={() => void save()}
        isLoading={isLoading}
        isDisabled={text === initial}
      >
        {t("Save summary")}
      </Button>
    </Stack>
  );
};
